/**
 * @fileoverview Shared schema for the Code Health dashboard artifact
 * (`backend/data/code-metrics.json`). Generated by `npm run metrics` and
 * served via GET /api/code-health.
 */

import { z } from 'zod';

export const CodeHealthCoverageSchema = z.object({
  overall: z.number(),
  byArea: z.record(z.string(), z.number()),
});

export const CodeHealthLintRuleSchema = z.object({
  ruleId: z.string(),
  count: z.number(),
});

export const CodeHealthLintFileSchema = z.object({
  file: z.string(),
  count: z.number(),
});

export const CodeHealthLintSchema = z.object({
  errors: z.number(),
  warnings: z.number(),
  topRules: z.array(CodeHealthLintRuleSchema).optional(),
  topFiles: z.array(CodeHealthLintFileSchema).optional(),
});

export const CodeHealthDependencySchema = z.object({
  name: z.string(),
  current: z.string(),
  wanted: z.string(),
  latest: z.string(),
  type: z.enum(['prod', 'dev']),
});

export const CodeHealthAdvisorySchema = z.object({
  name: z.string(),
  severity: z.string(),
  title: z.string(),
});

export const CodeHealthVulnerabilitiesSchema = z.object({
  total: z.number(),
  bySeverity: z.record(z.string(), z.number()),
  advisories: z.array(CodeHealthAdvisorySchema).optional(),
});

export const CodeHealthDeadCodeDetailsSchema = z.object({
  files: z.array(z.string()).optional(),
  dependencies: z.array(z.string()).optional(),
});

export const CodeHealthDeadCodeSchema = z.object({
  unusedFiles: z.number(),
  unusedDependencies: z.number(),
  unusedExports: z.number(),
  details: CodeHealthDeadCodeDetailsSchema.optional(),
});

export const CodeHealthDuplicationCloneSchema = z.object({
  firstFile: z.string(),
  secondFile: z.string(),
  lines: z.number(),
});

export const CodeHealthDuplicationDetailsSchema = z.object({
  clones: z.array(CodeHealthDuplicationCloneSchema).optional(),
});

export const CodeHealthDuplicationSchema = z.object({
  clones: z.number(),
  duplicatedLines: z.number(),
  totalLines: z.number(),
  percentage: z.number(),
  files: z.number(),
  details: CodeHealthDuplicationDetailsSchema.optional(),
});

export const CodeHealthSchema = z.object({
  generatedAt: z.string().nullable(),
  coverage: CodeHealthCoverageSchema.optional(),
  lint: CodeHealthLintSchema.optional(),
  dependencies: z.array(CodeHealthDependencySchema).optional(),
  vulnerabilities: CodeHealthVulnerabilitiesSchema.optional(),
  deadCode: CodeHealthDeadCodeSchema.optional(),
  duplication: CodeHealthDuplicationSchema.optional(),
});

export const CodeHealthEmptySchema = z.object({
  generatedAt: z.null(),
});

/**
 * Status of an on-server metrics regeneration job, polled by the "Re-run
 * metrics" button while `npm run metrics` runs in the background.
 */
export const CodeHealthRerunStatusSchema = z.object({
  status: z.enum(['idle', 'running', 'success', 'error']),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  error: z.string().nullable(),
});

export type CodeHealthCoverage = z.infer<typeof CodeHealthCoverageSchema>;
export type CodeHealthLint = z.infer<typeof CodeHealthLintSchema>;
export type CodeHealthDependency = z.infer<typeof CodeHealthDependencySchema>;
export type CodeHealthVulnerabilities = z.infer<typeof CodeHealthVulnerabilitiesSchema>;
export type CodeHealthDeadCode = z.infer<typeof CodeHealthDeadCodeSchema>;
export type CodeHealthDuplication = z.infer<typeof CodeHealthDuplicationSchema>;
export type CodeHealth = z.infer<typeof CodeHealthSchema>;
export type CodeHealthRerunStatus = z.infer<typeof CodeHealthRerunStatusSchema>;
